/**
 * First-class GenUI bento tile components (widget registry).
 * Callers: InsightsGenuiBento cell renderer.
 * Optional cell.icon from WORKFLOW_ICON_KEYS (flow-builder catalog).
 */

"use client";

import type { ReactNode } from "react";
import { GenuiSectionIcon } from "./GenuiSectionIcon";
import { InsightsGenuiBar } from "./InsightsGenuiBar";
import { InsightsGenuiTable } from "./InsightsGenuiTable";
import { InsightsMessageText } from "./InsightsMessageText";
import type {
  GenuiActionsPayload,
  GenuiBarPayload,
  GenuiCaveatPayload,
  GenuiEstatePayload,
  GenuiHeaderPayload,
  GenuiKpiPayload,
  GenuiMetaPayload,
  GenuiTablePayload,
  GenuiTextPayload,
  InsightsGenuiBentoCell,
} from "./genui-bento-types";
import { cn } from "@/shared/lib/utils/cn";

function TileShell({
  children,
  className,
  live,
}: {
  children: ReactNode;
  className?: string;
  live?: boolean;
}) {
  return (
    <div className={cn("h-full min-w-0 p-3.5", live && "bg-primary/[0.02]", className)}>
      {children}
    </div>
  );
}

export function GenuiHeaderTile({
  payload,
  icon,
  live,
}: {
  payload: GenuiHeaderPayload;
  icon?: string;
  live?: boolean;
}) {
  return (
    <TileShell live={live}>
      <div className="flex items-start gap-2.5">
        <GenuiSectionIcon icon={icon} hint={payload.title} kind="header" />
        <div className="min-w-0 flex-1 space-y-1.5">
          <h2 className="text-[16px] font-semibold leading-snug tracking-tight text-foreground">
            {payload.title}
          </h2>
          {payload.subtitle ? (
            <p className="text-[14px] leading-[1.55] text-foreground/80">{payload.subtitle}</p>
          ) : null}
          {payload.frame ? (
            <p className="text-[12px] leading-5 text-foreground/55">{payload.frame}</p>
          ) : null}
        </div>
      </div>
    </TileShell>
  );
}

export function GenuiKpiTile({
  payload,
  icon,
  live,
}: {
  payload: GenuiKpiPayload;
  icon?: string;
  live?: boolean;
}) {
  return (
    <TileShell live={live} className="flex items-start gap-2">
      <GenuiSectionIcon icon={icon} hint={`${payload.label} ${payload.value}`} kind="kpi" />
      <div className="flex min-w-0 flex-1 flex-col justify-start gap-1">
        <p className="text-[12px] font-medium uppercase tracking-wide text-foreground/55">
          {payload.label}
        </p>
        <p className="text-[22px] font-semibold tabular-nums leading-tight text-foreground">
          {payload.value}
        </p>
        {payload.hint ? (
          <p className="text-[12px] leading-5 text-foreground/55">{payload.hint}</p>
        ) : null}
      </div>
    </TileShell>
  );
}

export function GenuiActionsTile({
  payload,
  icon,
  live,
}: {
  payload: GenuiActionsPayload;
  icon?: string;
  live?: boolean;
}) {
  return (
    <div className={cn("flex h-full min-w-0 flex-col bg-background", live && "bg-primary/[0.02]")}>
      <div className="flex items-center gap-2.5 border-b border-border/50 bg-muted/30 px-3.5 py-2.5 text-[16px] font-semibold leading-snug text-foreground">
        <GenuiSectionIcon icon={icon} hint="actions next steps" kind="actions" />
        <span className="min-w-0">This week</span>
      </div>
      <ol className="list-decimal space-y-1.5 px-3.5 py-3 pl-8 text-[14px] leading-[1.55] text-foreground">
        {payload.items.map((item, i) => (
          <li key={`${i}-${item.slice(0, 24)}`}>{item}</li>
        ))}
      </ol>
    </div>
  );
}

export function GenuiEstateTile({
  payload,
  icon,
  live,
}: {
  payload: GenuiEstatePayload;
  icon?: string;
  live?: boolean;
}) {
  return (
    <TileShell live={live} className="space-y-2">
      <div className="flex items-center gap-2">
        <GenuiSectionIcon icon={icon} hint="estate coverage" kind="estate" />
        <p className="text-[12px] font-medium uppercase tracking-wide text-foreground/55">Estate</p>
      </div>
      <ul className="space-y-1.5">
        {payload.items.map((item) => (
          <li
            key={item.id}
            className="flex items-baseline justify-between gap-2 text-[14px] leading-[1.55]"
          >
            <span className="min-w-0 truncate text-foreground">{item.label}</span>
            <span className="shrink-0 text-[12px] uppercase tracking-wide text-foreground/55">
              {item.status}
            </span>
          </li>
        ))}
      </ul>
    </TileShell>
  );
}

export function GenuiCaveatTile({
  payload,
  icon,
  live,
}: {
  payload: GenuiCaveatPayload;
  icon?: string;
  live?: boolean;
}) {
  return (
    <TileShell live={live} className="space-y-1.5">
      <div className="flex items-center gap-2">
        <GenuiSectionIcon icon={icon} hint={payload.text} kind="caveat" />
        <p className="text-[12px] font-medium uppercase tracking-wide text-foreground/55">Caveat</p>
      </div>
      <p className="text-[14px] leading-[1.55] text-foreground/80">{payload.text}</p>
    </TileShell>
  );
}

export function GenuiMetaTile({ payload, live }: { payload: GenuiMetaPayload; live?: boolean }) {
  const bits = [payload.frame, payload.asOf, payload.investigationId].filter(Boolean);
  if (!bits.length && !live) return null;
  return (
    <div className="min-w-0 px-3.5 py-2 text-[12px] leading-5 text-foreground/55">
      {bits.length ? (
        bits.join(" · ")
      ) : (
        <span className="inline-block h-3 w-40 animate-pulse rounded bg-muted" />
      )}
    </div>
  );
}

export function GenuiTextTile({
  payload,
  icon,
  live,
}: {
  payload: GenuiTextPayload;
  icon?: string;
  live?: boolean;
}) {
  return (
    <TileShell live={live} className="!p-3">
      <div className="flex items-start gap-2.5">
        <GenuiSectionIcon icon={icon} hint={payload.markdown.slice(0, 80)} kind="text" />
        <div className="min-w-0 flex-1">
          <InsightsMessageText text={payload.markdown} />
        </div>
      </div>
    </TileShell>
  );
}

/** Registry: kind → React tile. Unknown kinds never reach here (parser rejects). */
export function GenuiBentoCellView({
  cell,
  live,
}: {
  cell: InsightsGenuiBentoCell;
  live?: boolean;
}) {
  switch (cell.kind) {
    case "header":
      return (
        <GenuiHeaderTile
          payload={cell.payload as GenuiHeaderPayload}
          icon={cell.icon}
          live={live}
        />
      );
    case "kpi":
      return (
        <GenuiKpiTile payload={cell.payload as GenuiKpiPayload} icon={cell.icon} live={live} />
      );
    case "actions":
      return (
        <GenuiActionsTile
          payload={cell.payload as GenuiActionsPayload}
          icon={cell.icon}
          live={live}
        />
      );
    case "estate":
      return (
        <GenuiEstateTile
          payload={cell.payload as GenuiEstatePayload}
          icon={cell.icon}
          live={live}
        />
      );
    case "caveat":
      return (
        <GenuiCaveatTile
          payload={cell.payload as GenuiCaveatPayload}
          icon={cell.icon}
          live={live}
        />
      );
    case "meta":
      return <GenuiMetaTile payload={cell.payload as GenuiMetaPayload} live={live} />;
    case "text":
      return (
        <GenuiTextTile payload={cell.payload as GenuiTextPayload} icon={cell.icon} live={live} />
      );
    case "table": {
      const p = cell.payload as GenuiTablePayload;
      return (
        <InsightsGenuiTable
          spec={{ type: "table", title: p.title, columns: p.columns, rows: p.rows }}
          icon={cell.icon}
          live={live}
        />
      );
    }
    case "bar": {
      const p = cell.payload as GenuiBarPayload;
      return (
        <InsightsGenuiBar
          spec={{ type: "bar", title: p.title, x: p.x, y: p.y, data: p.data }}
          icon={cell.icon}
          live={live}
        />
      );
    }
    default:
      return null;
  }
}
